import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { WaterModal } from './water-modal/water-modal';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { WindOffshoreModal } from './wind-offshore-modal/wind-offshore-modal';
import { CombinedHeatPowerModal } from './combined-heat-power-modal/combined-heat-power-modal';
import { SmallestCapacityModal } from './smallest-capacity-modal/smallest-capacity-modal';
import { LargestCapacityModal } from './largest-capacity-modal/largest-capacity-modal';
import { CommonModule } from '@angular/common';
import { environment } from '../environments/environment';
import { GenerationTechnology } from './generation-technology.enum';
import { distinctUntilChanged, filter } from 'rxjs';
import { MatSliderModule } from '@angular/material/slider';
import { ComponentType } from '@angular/cdk/portal';
import { IframeResizeService } from './iframe-resize.service';
import { AbstractTrackEventsService } from './analytics/abstract-track-events.service';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    MatCardModule,
    MatRadioModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatSliderModule,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private readonly trackEventsService = inject(AbstractTrackEventsService);
  private readonly dialog = inject(MatDialog);
  // IframeResizeService should be initialized but might be not used
  private readonly resizeService = inject(IframeResizeService);

  protected readonly title = signal('offer-tool-dispatcher-frontend');
  form!: FormGroup;
  showAdditionalQuestions = signal(false);

  protected readonly windOnshoreTooltip = signal(
    'Windkraftanlagen an Land – sie stehen z. B. auf Feldern oder Hügeln und nutzen den Wind auf dem Festland zur Stromerzeugung.',
  );
  protected readonly windOffshoreTooltip = signal(
    'Windkraftanlagen im Meer – sie stehen weit draußen im Wasser und nutzen den stärkeren, stetigeren Wind über dem Meer für die Stromerzeugung.',
  );

  protected readonly generationTechnologyOptions = signal([
    { value: GenerationTechnology.SOLAR, label: 'Solar' },
    {
      value: GenerationTechnology.WIND_ONSHORE,
      label: 'Wind-Onshore',
      tooltip: this.windOnshoreTooltip,
    },
    {
      value: GenerationTechnology.WIND_OFFSHORE,
      label: 'Wind-Offshore',
      tooltip: this.windOffshoreTooltip,
    },
    { value: GenerationTechnology.COMBINED_HEAT_POWER, label: 'Kraft-Wärme-Kopplung (KWK)' },
    { value: GenerationTechnology.WATER, label: 'Wasser' },
  ]);

  protected readonly isForOwnConsumptionOptions = signal([
    { value: true, label: 'Ja' },
    { value: false, label: 'Nein' },
  ]);

  protected readonly installedCapacityOptions = signal<string[]>([
    'unter 100 kW',
    '>100 kW – 1 MW',
    '>1 MW – 5 MW',
    '>5 MW – 10 MW',
    'über 10 MW',
  ]);

  ngOnInit(): void {
    this.form = new FormGroup({
      generationTechnology: new FormControl('', Validators.required),
      isForOwnConsumption: new FormControl(null, Validators.required),
      installedCapacity: new FormControl(1, Validators.required),
    });

    this.form.get('generationTechnology')?.valueChanges.subscribe((value) => {
      this.showAdditionalQuestions.set(
        value === GenerationTechnology.SOLAR || value === GenerationTechnology.WIND_ONSHORE,
      );

      if (value === GenerationTechnology.WATER) {
        this.openWaterModal();
      } else if (value === GenerationTechnology.WIND_OFFSHORE) {
        this.openWindOffShoreModal();
      } else if (value === GenerationTechnology.COMBINED_HEAT_POWER) {
        this.openCombinedHeatPowerModal();
      }
    });

    this.form
      .get('installedCapacity')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        filter((v) => v === 0 || v === 4),
      )
      .subscribe((value) => {
        if (value === 0) {
          this.openSmallestCapacityModal();
        } else if (value === 4) {
          this.openLargestCapacityModal();
        }
      });
    this.trackEventRoutingStarted();
  }

  openModalWindow(component: ComponentType<unknown>) {
    this.dialog.open(component, {
      width: '648px',
      maxWidth: 'none',
    });
  }

  openWaterModal(): void {
    this.openModalWindow(WaterModal);
  }

  openWindOffShoreModal(): void {
    this.openModalWindow(WindOffshoreModal);
  }

  openCombinedHeatPowerModal(): void {
    this.openModalWindow(CombinedHeatPowerModal);
  }

  openSmallestCapacityModal(): void {
    this.openModalWindow(SmallestCapacityModal);
  }

  openLargestCapacityModal(): void {
    this.openModalWindow(LargestCapacityModal);
  }

  setInstalledCapacity(index: number): void {
    this.form.get('installedCapacity')?.setValue(index);
  }

  onClick(): void {
    const generationTechnology = this.form.get('generationTechnology')?.value;
    const isForOwnConsumption = this.form.get('isForOwnConsumption')?.value;
    const installedCapacity = this.form.get('installedCapacity')?.value;
    let baseUrl;

    if (
      isForOwnConsumption === true &&
      (generationTechnology === GenerationTechnology.WIND_ONSHORE ||
        generationTechnology === GenerationTechnology.SOLAR)
    ) {
      baseUrl = environment.oldOfferToolUrl;
    } else if (
      (isForOwnConsumption === false &&
        generationTechnology === GenerationTechnology.SOLAR &&
        (installedCapacity === 1 || installedCapacity === 2)) ||
      (generationTechnology === GenerationTechnology.WIND_ONSHORE && installedCapacity === 1)
    ) {
      baseUrl = environment.oldOfferToolUrl;
    } else {
      baseUrl = environment.newOfferToolUrl;
    }
    const url = new URL(baseUrl);
    const paramName = this.getTechnologyQueryParamName(baseUrl);
    const paramValue = this.getTechnologyQueryParamValue(generationTechnology, baseUrl);
    if (paramName && paramValue) {
      url.searchParams.append(paramName, paramValue);
    }
    this.trackEventRoutingCompleted(generationTechnology, isForOwnConsumption, installedCapacity);
    window.location.href = url.href;
  }

  private getTechnologyQueryParamName(baseUrl: string): string | null {
    if (baseUrl === environment.oldOfferToolUrl) {
      return 'energyTypeName';
    } else if (baseUrl === environment.newOfferToolUrl) {
      return 'technology';
    }
    return null;
  }

  private getTechnologyQueryParamValue(
    technology: GenerationTechnology,
    baseUrl: string,
  ): string | null {
    if (baseUrl === environment.oldOfferToolUrl) {
      switch (technology) {
        case GenerationTechnology.SOLAR:
          return 'pv';
        case GenerationTechnology.WIND_ONSHORE:
          return 'wind';
        default:
          return null;
      }
    } else if (baseUrl === environment.newOfferToolUrl) {
      return technology;
    }
    return null;
  }

  isNextButtonEnabled(): boolean {
    const generationTechnology = this.form.get('generationTechnology')?.value || null;
    const installedCapacity = this.form.get('installedCapacity')?.value;

    return (
      this.form.valid &&
      (generationTechnology === GenerationTechnology.SOLAR ||
        generationTechnology === GenerationTechnology.WIND_ONSHORE) &&
      (installedCapacity === 1 || installedCapacity === 2 || installedCapacity === 3)
    );
  }

  trackEventRoutingStarted(): void {
    const event = {
      event: 'routing_started',
      eventCategory: 'Form',
      eventAction: 'view',
      eventLabel: 'Routing Tool',
    };
    this.trackEventsService.trackEvent(event);
  }

  trackEventRoutingCompleted(
    technology: GenerationTechnology,
    selfConsumption: boolean,
    capacityValueIndex: number,
  ): void {
    const event = {
      event: 'routing_completed',
      eventCategory: 'Form',
      eventAction: 'click',
      eventLabel: 'Weiter',
      technology_type: technology,
      self_consumption: selfConsumption,
      installed_capacity: this.installedCapacityOptions()[capacityValueIndex],
      target_funnel: 'new', // TODO set when 'existing' is available
    };
    this.trackEventsService.trackEvent(event);
  }
}
